import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import UnifiedInquirySection from '../../components/features/UnifiedInquirySection';
import { getSolutions } from '../../api/content';
import './Solutions.css';

const Solutions = () => {
    const navigate = useNavigate();
    // `/solutions/{코드}` 로 들어오면 그 업종 허브가 된다 (설계 03 §2-6).
    // 대표뿐 아니라 **추가로 지정된 솔루션도 함께 실린다** — 다중 분류의 이득을
    // URL 이 아니라 이 페이지에서 회수하는 구조다.
    const { category } = useParams();
    const [solutions, setSolutions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSolutions();
    }, []);

    const loadSolutions = async () => {
        try {
            const data = await getSolutions();
            setSolutions(data);
        } catch (error) {
            console.error('Failed to load solutions', error);
        } finally {
            setLoading(false);
        }
    };

    /*
     * `/solutions/{숫자}` 로 앱 안에서 넘어온 경우 정본 경로로 갈아탄다.
     *
     * 이 주소는 라우터가 업종 허브로 매칭하는데, 숫자는 어떤 업종 코드와도 맞지
     * 않아 결국 **전체 목록을 다시 그린다** — 주소만 바뀌고 화면은 그대로인 것처럼
     * 보인다. 브라우저를 새로 고치면 그때서야 서버가 301 을 내므로, 원인을
     * 찾기 어려운 종류의 고장이다.
     *
     * 직접 들어온 요청은 nginx→NestJS 가 실제 301 을 내므로 여기까지 오지 않는다.
     * 여기서 막는 것은 앱 안에 남은 숫자 링크뿐이다.
     */
    useEffect(() => {
        if (!category || !/^\d+$/.test(category) || !solutions.length) return;
        const hit = solutions.find((s) => String(s.id) === category);
        navigate(hit?.path ?? '/solutions', { replace: true });
    }, [category, solutions, navigate]);

    const inHub = Boolean(category) && !/^\d+$/.test(category);
    const visible = inHub
        ? solutions.filter((s) => (s.categories || []).some((c) => c.slug === category))
        : solutions;
    const hubLabel = inHub
        ? (solutions.flatMap((s) => s.categories || []).find((c) => c.slug === category)?.label ?? category)
        : null;

    return (
        <div className="solutions-page">
            <div className="page-hero">
                <div className="container">
                    {inHub ? (
                        <>
                            <h1 className="page-title">{hubLabel} 솔루션</h1>
                            <p className="page-subtitle">
                                {hubLabel} 현장에 맞춰 쓰이는 가까운솔루션의 솔루션입니다.<br />
                                <Link to="/solutions" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                    전체 솔루션 보기
                                </Link>
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="page-title">솔루션 소개</h1>
                            <p className="page-subtitle">
                                가까운솔루션의 대표 솔루션을 소개합니다.<br />
                                복잡한 과정을 덜어내고, 현장에 꼭 필요한 기능만을 담았습니다.
                            </p>
                        </>
                    )}
                </div>
            </div>

            {/* Dynamic Solutions from DB */}
            {visible.length > 0 && (
                <section style={{ padding: '4rem 0', backgroundColor: 'white' }}>
                    <div className="container">
                        <div className="solutions-list" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2rem'
                        }}>
                            {visible.map(solution => (
                                <div
                                    key={solution.id}
                                    className="solution-card-horizontal"
                                    onClick={() => navigate(solution.path || `/solutions/${solution.id}`)}
                                    style={{
                                        cursor: 'pointer',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        background: 'white',
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto 1fr',
                                        minHeight: '200px',
                                        alignItems: 'center'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                    }}
                                >
                                    {/* Left: Text */}
                                    <div style={{ padding: '2rem' }}>
                                        {solution.category && (
                                            <span style={{
                                                background: 'var(--primary-color)',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '0.75rem',
                                                marginBottom: '0.75rem',
                                                display: 'inline-block'
                                            }}>
                                                {solution.categoryLabel || solution.category}
                                            </span>
                                        )}
                                        <h3 style={{
                                            fontSize: '1.4rem',
                                            marginBottom: '0.75rem',
                                            color: '#333',
                                            lineHeight: '1.4'
                                        }}>
                                            {solution.title}
                                        </h3>
                                        {solution.subtitle && (
                                            <p style={{
                                                fontSize: '0.95rem',
                                                color: '#666',
                                                lineHeight: '1.6'
                                            }}>
                                                {solution.subtitle}
                                            </p>
                                        )}
                                    </div>

                                    {/* Center: Coral Pink Divider */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        padding: '1rem 0'
                                    }}>
                                        <div style={{
                                            width: '4px',
                                            height: '65%',
                                            background: 'var(--primary-color)',
                                            borderRadius: '4px'
                                        }} />
                                    </div>

                                    {/* Right: Thumbnail */}
                                    <div style={{
                                        height: '100%',
                                        minHeight: '180px',
                                        backgroundImage: solution.thumbnail ? `url(${solution.thumbnail})` : 'linear-gradient(135deg, #f5f5f5, #e0e0e0)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }} />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Default Content (shown when no DB solutions, or always as additional info) */}
            {(loading || solutions.length === 0) && (
                <>
                    {/* ESL Value Proposition */}
                    <section className="values-section" style={{ padding: '4rem 0', backgroundColor: 'white' }}>
                        <div className="container">
                            <h2 className="section-title" style={{ fontSize: '1.8rem', textAlign: 'center', marginBottom: '3rem' }}>Why Smart ESL?</h2>
                            <div className="values-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem' }}>
                                <div className="value-card" style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <h3 className="value-title" style={{ color: 'var(--primary-color)', marginBottom: '1rem', fontSize: '1.3rem' }}>압도적 효율</h3>
                                    <p>인쇄, 코팅, 부착 등<br />반복되는 단순 업무를<br />100% 자동화합니다.</p>
                                </div>
                                <div className="value-card" style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <h3 className="value-title" style={{ color: 'var(--primary-color)', marginBottom: '1rem', fontSize: '1.3rem' }}>세련된 공간</h3>
                                    <p>종이 자국 없는 깔끔한<br />디지털 디스플레이로<br />공간의 품격을 높입니다.</p>
                                </div>
                                <div className="value-card" style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <h3 className="value-title" style={{ color: 'var(--primary-color)', marginBottom: '1rem', fontSize: '1.3rem' }}>초간편 관리</h3>
                                    <p>전용 프로그램 설치 없이<br />웹사이트 접속만으로<br />모든 명패를 제어합니다.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {loading && (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            로딩 중...
                        </div>
                    )}
                </>
            )}

            <UnifiedInquirySection title="실제로 어떻게 작동하는지 무료로 체험해보세요." buttonText="💻 솔루션 무료 데모 신청" buttonAction="DEMO" />
        </div>
    );
};

export default Solutions;
